import { UUID } from "../auth/service";

export type PhoneNumber = number;
export interface ILocation {
  lat: number;
  lon: number;
}

export enum WeekDay {
  SATURDAY = "SATURDAY",
  SUNDAY = "SUNDAY",
  MONDAY = "MONDAY",
  TUESDAY = "TUESDAY",
  WEDNESDAY = "WEDNESDAY",
  THURSDAY = "THURSDAY",
  FRIDAY = "FRIDAY",
}

export enum OrderWeekDay {
  SATURDAY,
  SUNDAY,
  MONDAY,
  TUESDAY,
  WEDNESDAY,
  THURSDAY,
  FRIDAY,
}

export interface WorkingHours {
  start: string | number;
  end: string | number;
}

export interface WorkingDay {
  weekDay: WeekDay;
  workingHours: WorkingHours;
  closed: boolean;
}
export interface DeliveryWorkingDay {
  weekDay: WeekDay;
  deliveryHours: WorkingHours;
  gapHours: WorkingHours;
  closed: boolean;
}
export interface FlatWorkingDay {
  [weekDay: string]: {
    weekDay: WeekDay;
    workingHours: WorkingHours;
    closed: boolean;
  };
}

export interface IFeature {
  uuid: UUID;
  data: {
    name: string;
    cities: string[];
    description?: string;
    photo?: string;
    delivery?: boolean;
    dineOut?: boolean;
  };
}

export interface ITag {
  uuid: UUID;
  data: {
    name: string;
    description?: string;
  };
}

export interface ICuisines {
  uuid: UUID;
  data: {
    name: string;
  };
}

export class DeliveryData {
  deliveryFee: number;
  deliveryDuration: string;
}

export interface IWorkingHours {
  start: string;
  end: string;
}

export interface ISizeAggregate {
  sizeUUID: UUID;
  name: string;
  price: number;
  oldPrice?: number;
  itemSizeUUID: UUID;
}

export interface ExtraCategoryAggregateData {
  name: string;
  description?: string;
  categoryType: CategoryTypeEnum;
  items: IItemAggregate[];
  availableFromTime?: string;
  availableToTime?: string;
  minimumAllowedExtra: number;
  maximumAllowedExtra: number;
}
export interface ExtraCategoryAggregate {
  uuid: UUID;
  data: ExtraCategoryAggregateData;
}

export interface ScannedMenuResponse {
  name: string;
  imageUrls: string[];
}

export interface CroppedPhotoResponse {
  photoUUID: UUID;
  photoURL: string;
  photoY: number;
}
export interface BranchAggregateData {
  location: ILocation;
  address: string;
  phoneNumbers: PhoneNumber[];
  dailyWorkingHours: WorkingDay[];
  dailyDeliveryHours: DeliveryWorkingDay[];
  deliveryAreaZones: DeliveryAreaZones[];
  deliveryDetails: IDeliveryDetails;
  tags: Tag[];
  features: Feature[];
  orderingEnabled: boolean;
  area: AreaUUIDnData;
  menus: IMenuAggregate[];
  scannedMenus: ScannedMenuResponse[];
  distance?: number;
  coverPhoto?: CroppedPhotoResponse;
}

export interface IDeliveryDetails {
  deliverToUser: boolean;
  deliveryFee?: number;
  deliveryDuration?: string;
}

export interface IWorkingSummary {
  interval: string;
  time: string;
}

export interface IBranchData {
  address: string;
  location: ILocation;
  phoneNumbers: PhoneNumber[];
  currentDistance: number;
  workingDays: IWorkingSummary[];
  tags: ITag[];
  features: IFeature[];
  deliveryData?: DeliveryData;
  orderingEnabled: boolean;
  workingHours: IWorkingHours;
  open: Boolean;
}

export interface IBranch {
  uuid: UUID;
  data: IBranchData;
}

export interface IAreaBranchesResponse {
  uuid: UUID;
  data: BranchAggregateData;
}

export interface IAreaBranches {
  area: UUID;
  branches: IBranch[];
}

export interface BranchAggregate {
  uuid: UUID;
  data: BranchAggregateData;
}

export interface IAreaBranch {
  area: UUID;
  branch: IBranch;
}

export interface IDeliveryArea {
  uuid: UUID;
  data: {
    name: string;
    location: ILocation;
  };
  shortName?: string;
}

export interface AreaData {
  name: string;
  otherName?: string;
  centerLocation: ILocation;
  orderingEnabled: boolean;
}

export interface AreaUUIDnData {
  uuid: UUID;
  data: AreaData;
}
export interface ZoneUUIDnData {
  zone: Zone;
  deliveryFee?: number;
  deliveryDuration?: number;
}
export interface Zone {
  uuid: UUID;
  ref: {
    areaUUID: UUID;
  };
  data: {
    name: string;
    otherName?: string;
    centerLocation: ILocation;
  };
}
export interface DeliveryAreaZones {
  area: AreaUUIDnData;
  zones: ZoneUUIDnData[];
}

export interface TagData {
  name: string;
  description?: string;
  photo?: string;
  delivery?: boolean;
  dineOut?: boolean;
}

export interface Tag {
  uuid: UUID;
  data: TagData;
}

export interface FeatureData {
  name: string;
  description?: string;
  photo: string;
}

export interface Feature {
  uuid: UUID;
  data: FeatureData;
}

export interface CompactBranchAggregateData {
  location: ILocation;
  address: string;
  phoneNumbers: PhoneNumber[];
  dailyWorkingHours: WorkingDay[];
  dailyDeliveryHours: WorkingDay[];
  deliveryAreaZones: DeliveryAreaZones[];
  tags: Tag[];
  features: Feature[];
  orderingEnabled: boolean;
  area: AreaUUIDnData;
}

export interface CompactBranchAggregate {
  uuid: UUID;
  data: CompactBranchAggregateData;
}

export interface Cuisine {
  uuid: UUID;
  data: {
    name: string;
  };
}

export interface ReviewsRating {
  rating: number;
  numberOfReviews: number;
}

export interface PhotoData {
  url: string;
  uuid: UUID;
}

export interface AreaBranches {
  area: AreaUUIDnData;
  branches: CompactBranchAggregate[];
}

export interface RestaurantAggregateResponseData {
  name: string;
  logo?: string;
  coverPhoto?: string;
  description?: string;
  shortNumber?: string;
  shortCode: string;
  facebookLink?: string;
  twitterLink?: string;
  youtubeLink?: string;
  website?: string;
  closed: boolean;
  areasBranches: AreaBranches[];
  currentBranch: BranchAggregate;
  tags: Tag[];

  cuisines: Cuisine[];

  minimumOrderValue: number;
  taxRate: number;
  shareLink: string;
  reviewsRating: ReviewsRating;
  photos: PhotoData[];
  favorite: boolean;
  deliveryAreasUUIDs: UUID[];
  url: string;
}

export interface RestaurantAggregateResponse {
  uuid: UUID;
  data: RestaurantAggregateResponseData;
}
export interface BranchMenusResponse {
  commonTagUUID: string;
  menus: IMenuAggregate;
}
export interface IArea {
  uuid: UUID;
  ref: {
    cityId: number;
  };
  data: {
    name: string;
    location: {
      latitude: number;
      longitude: number;
    };
  };
}

export interface ICity {
  uuid: UUID;
  ref: {
    countryId: number;
  };
  data: {
    name: string;
  };
}

export interface IZone {
  uuid: UUID;
  data: {
    name: string;
    otherName?: string;
  };
}
export interface Area {
  uuid: UUID;
  ref: {
    cityUUID: UUID;
  };
  data: AreaData;
}
export interface City {
  uuid: UUID;
  ref: {
    countryUUID: UUID;
  };
  data: {
    name: string;
    otherName?: string;
  };
}
export interface ReportType {
  uuid: UUID;
  data: {
    name: string;
  };
}
export interface AggregateLookupResponse {
  areas: Area[];
  cities: City[];
  zones: Zone[];
  moods: Tag[];
  features: Feature[];
  deliveryCategories: Tag[];
  dineOutCategories: Tag[];
}

export interface IScannedMenuResponseData {
  name: string;
  imageUrls: string[];
}

export interface IScannedMenuResponse {
  uuid: UUID;
  data: IScannedMenuResponseData[];
}

type Availability = {
  timeAvailability: {
    availableFromTime?: string;
    availableToTime?: string;
    availableNow: boolean;
  };
  availableInBranch?: boolean;
  availableForDelivery: boolean;
};

export interface IItemAggregate {
  uuid: UUID;
  name: string;
  description?: string;
  sizes: ISizeAggregate[];
  availability: Availability;
  likesCount: number;
  isLikedByUser: boolean;
  photoUrl: string;
  photos: string[];
  hasExtras: boolean;
  customizable: boolean;
}

export enum CategoryTypeEnum {
  REGULAR = "REGULAR",
  EXTRAS = "EXTRAS",
  BYO = "BYO",
}

export interface ICategoryAggregate {
  uuid: UUID;
  name: string;
  description?: string;
  type: CategoryTypeEnum;
  availability: Availability;
  items: IItemAggregate[];
}

export interface IMenuAggregate {
  uuid: UUID;
  name: string;
  availableForDelivery: boolean;
  categories: ICategoryAggregate[];
}

export interface RestaurantCompact {
  uuid: string;
  name: string;
  shortCode: string;
}

export interface UserAddressResponse {
  uuid: string;
  ref: {
    cityUUID: string;
    areaUUID: string;
    zoneUUID: string;
  };
  data: {
    fullName: string;
    phoneNumber: string;
    location: ILocation;
    address: string;
    deliveryInstructions: string;
    buildingNumber: string;
    floor: string;
    apartmentNumber: string;
    companyName: string;
    company: boolean;
    default: boolean;
  };
}
interface UserProfileResponse {
  userUUID: UUID;
  data: {
    avatar?: string;
    name: string;
    email: string;
    bio?: string;
    gender?: string;
  };
}
interface RestaurantCompactResponse {
  uuid: UUID;
  data: {
    logo: string;
    name: string;
    shortCode: string;
  };
}
interface ItemCompactResponse {
  uuid: UUID;
  data: {
    name: string;
  };
}

export interface ItemPhotoCompactResponse {
  uuid: UUID;
  ref: {
    user: UserProfileResponse;
    restaurant: RestaurantCompactResponse;
    item?: ItemCompactResponse;
  };
  data: {
    photoUrl: string;

    featured: boolean;
    yumCount: number;
    commentCount: number;
  };
  trace: Trace;
}

export interface SocialCounts {
  followerCount: number;
  followedCount: number;
  favoriteCount: number;
  likeCount: number;
  yumCount: number;
  photoCount: number;
  reviewCount: number;
}

export interface PublicUserResponse {
  uuid: UUID;
  data: {
    shortCode: string;
    name: string;
    followed: boolean;
    avatar?: string;
    bio?: string;
    socialCounts: SocialCounts;
  };
}

export interface UserReviewResponse {
  uuid: UUID;
  ref: {
    user: PublicUserResponse;
    restaurant: RestaurantCompactResponse;
    branch?: BranchCompactResponse;
  };
  data: {
    text: string;
    rating: number;
    photos: ItemPhotoCompactResponse[];
    helpfulCount: number;
    helpful: boolean;
  };
  trace: Trace;
}

export interface TimelinePhotoResponse {
  count: number;
  page: number;
  data: ItemPhotoCompactResponse[];
}
export interface Trace {
  created: Signature;
  updated: Signature;
}
interface Signature {
  at: string;
  by: string;
}

export interface ItemPhotoResponse {
  uuid: UUID;
  ref: {
    photoUUID: UUID;
    userProfile: UserProfileResponse;
    restaurant: RestaurantCompactResponse;
    branch: BranchCompactResponse;
    item: ItemCompactResponse;
    reviewUUID?: UUID;
  };
  data: {
    caption?: string;
    suggestedTag?: string;
    location?: Location;
    featured: boolean;
    yums: number;
    comments: CommentResponse[];
  };
  trace: Trace;
}

export interface BranchCompactResponse {
  uuid: UUID;
  enAddress: string;
  arAddress: string;
}

export interface CommentResponse {
  uuid: UUID;
  ref: {
    userProfile: UserProfileResponse;
    itemPhotoUUID: UUID;
  };
  data: {
    text: string
  }
  trace: Trace
}


// header
export interface RestaurantHeaderResponse {
  branchUUID: string
  restaurantUUID: string,
  name: string,
  logo?: string,
  restaurantShortCode: string,
  branchShortCode: string,
  cuisines: CommonData[],
  reviewsRating: {
      numberOfReviews: number,
      rating: number
  },
  coverImage?: string
  paymentMethods: PaymentTypeEnum[],
  onlineOrdering: {
      deliveryFeesText: string,
      edt: number,
      fees: number
  },
  fleetEnabled: boolean,
  branchOrderingEnabled: boolean,
  restaurantOrderingEnabled: boolean,
  branchDeliversToLocation: boolean,
  openNow: boolean,
  secondsUntilOpen: number,
  secondsUntilClose: number,
  busy: boolean,
  acceptsPromos: boolean,
  branchLocation : Location,
  deliveryHours: DeliveryHours,
  branchAddress: string,
  minimumOrderValue: number,
  shortNumber: string | null, // can be null
  phoneNumbers?: PhoneNumber[]
  distance: number,
  isFavorite: boolean,
  shareLink: string;
  city: CommonData;
  area: CommonData;
  zone: CommonData;
}

interface CommonData {
  uuid: string;
  name: string;
}
export interface DayHour{
  from: number
  to: number
}
export interface DeliveryHours {
  [weekDay: string]: DayHour[]
}
export enum PaymentTypeEnum {
  CASH_ON_DELIVERY = 'CASH_ON_DELIVERY',
  CREDIT_CARD = 'CREDIT_CARD',
  WALLET = 'WALLET',
}

export interface DeliveryDay {
  weekDay: WeekDay;
  hours: {
    from: number
    to: number
  }[]
}

export interface DateTimeInterval{
  startDate: string;
  endDate:string;
}

export interface RecommendedPromo {
  allAreas: boolean;
  allRestaurants: string;
  allUsers: string;
  code: string;
  interval: DateTimeInterval;
  isStretch: boolean;
  maxMoneyValue: number;
  message: string;
  promoCategory: string;
  promoCodeUUID: string;
  promoMinOrderValue: number;
  promoUUID: string;
}
